import React, { useEffect, useRef, useState } from 'react';
import {
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { Button, Grid, Segment } from 'semantic-ui-react';
import { useNavigate } from 'react-router-dom';
import qs from 'query-string';

import {
  Typography,
  TermAndCondition,
  PaymentCourseInfo,
} from 'src/components/coreUI';
import Form from 'src/components/form';
import { PATH } from 'src/constants/app';
import { ENV } from 'src/env';
import { useAppDispatch, useAppSelector } from 'src/store';
import notification from 'src/store/ui/notification';
import { useForm, useURLState } from 'src/hooks';
import { PaymentIntent, StripeError } from '@stripe/stripe-js';
import { PaymentProcessParams } from 'src/thunks/payment';
import { validateCouponId } from 'src/thunks/coupon';
import { useResponsive } from 'ahooks';

type Props = {
  client_secret: string;
  course?: Model.Class;
  isExtension?: boolean;
};

const PaymentForm: React.FC<Props> = ({
  course,
  client_secret,
  isExtension,
}) => {
  const navigate = useNavigate();
  const stripe = useStripe();
  const elements = useElements();
  const dispatch = useAppDispatch();
  const { name, email } = useAppSelector((state) => state.ui.app.me!);
  const screen = useResponsive();

  const [completed, setCompleted] = useState<boolean>(false);
  const [type, setType] = useState<string>('card');
  const [{ cp_id, pr_id, expired_date }] = useURLState<PaymentProcessParams>(
    {},
  );
  const [paymentIntent, setPaymentIntend] = useState<PaymentIntent>();

  const [showTnd, setShowTnd] = useState(false);
  const showTndRef = useRef(false);

  const { submitForm, isSubmitting, getFieldInfo, setFieldValue } = useForm<
    Partial<Model.Me>
  >({
    initialValues: {
      name,
      email,
    },
    async onSubmit(value) {
      if (!stripe || !elements) return;
      let error: StripeError | undefined;

      if (cp_id) {
        const res = await dispatch(
          validateCouponId({ coupon_id: cp_id, class_price_id: pr_id }),
        );

        if (!validateCouponId.fulfilled.match(res)) {
          return;
        }
      }

      if (type === 'card') {
        const res = await stripe.confirmPayment({
          elements,
          confirmParams: {
            return_url: `${ENV.URL}/${PATH.PAYMENT_SUCCESS}/${
              course?.id
            }?isExtension=${!!isExtension}`,
            payment_method_data: {
              billing_details: { name: value.name, email: value.email },
            },
          },
        });

        error = res.error;
      } else {
        const res = await stripe.confirmPayNowPayment(client_secret, {
          return_url: `${ENV.URL}/${PATH.PAYMENT_SUCCESS}/${
            course?.id
          }?isExtension=${!!isExtension}`,
          payment_method: {
            billing_details: { name: value.name, email: value.email },
          },
        });

        if (res.error || (res.paymentIntent.last_payment_error as any)) {
          error = res.error || (res.paymentIntent.last_payment_error as any);
        }

        if (res.paymentIntent?.status === 'succeeded') {
          const params = qs.stringify({
            payment_intent: res.paymentIntent.id,
            payment_intent_client_secret: client_secret,
            cp_id,
            pr_id,
          });

          navigate(`/${PATH.PAYMENT_SUCCESS}/${course?.id}?${params}`);
        }
      }

      if (error) {
        dispatch(
          notification.on({
            type: 'error',
            message: (
              <span>
                {error.message}{' '}
                <a
                  href={error.doc_url}
                  className="text-underline text-primary"
                  target="_blank"
                  rel="noreferrer"
                >
                  More info
                </a>
              </span>
            ),
          }),
        );
      }
    },
  });

  useEffect(() => {
    const checkClientSecret = async () => {
      if (!stripe || !elements) return;

      const { paymentIntent, error } = await stripe.retrievePaymentIntent(
        client_secret,
      );

      setPaymentIntend(paymentIntent);

      if (paymentIntent?.receipt_email !== email) {
        setFieldValue('email', paymentIntent?.receipt_email);
        setFieldValue('name', '');
      }

      if (error) {
        navigate(PATH.FORBIDDEN);
      }

      if (paymentIntent?.status === 'succeeded') {
        navigate(`/${PATH.CLASS}/${course?.id}`);
      }
    };

    checkClientSecret();
  }, [stripe, elements]);

  return (
    <div style={{ position: 'relative' }}>
      <div
        style={{
          position: 'fixed',
          background: 'transparent',
          inset: 0,
          zIndex: 1000,
          display: showTnd ? 'block' : 'none',
        }}
      >
        <div
          style={{
            width: screen.sm ? `calc(100% - 23% * 2)` : 'calc(100% - 24px * 2)',
            margin: 'auto',
            marginTop: 24 + 16 + 1.5 * 2 * 16 + 80,
          }}
        >
          <Segment>
            <TermAndCondition
              loading={isSubmitting}
              handlePayment={submitForm}
              handleBack={() => setShowTnd(false)}
            />
          </Segment>
        </div>
      </div>

      <Segment style={{ visibility: showTnd ? 'hidden' : 'visible' }}>
        <Grid columns="2" stackable divided relaxed reversed="mobile">
          <Grid.Column width="9" stretched>
            <Typography.Title className="mt-0">CHECKOUT</Typography.Title>

            <Form>
              <Typography.Header>Billing Details</Typography.Header>
              <Form.Input
                label="EMAIL"
                placeholder="Email"
                {...getFieldInfo('email')}
                disabled={isSubmitting}
              />
              <Form.Input
                label="NAME"
                placeholder="Name"
                {...getFieldInfo('name')}
                disabled={isSubmitting}
              />

              <Typography.Header>
                <div className="mt-24" style={{ marginBottom: '-10px' }}>
                  Payment
                </div>
              </Typography.Header>
              <PaymentElement
                options={{
                  readOnly: isSubmitting,
                }}
                onChange={(event) => {
                  setCompleted(event.complete);
                  setType(event.value.type);
                }}
                onLoadError={(event) => navigate(PATH.NOT_FOUND)}
              />

              <div className="fs-14 mt-12 text-center">
                Accept our <span className="fw-bold">Terms and Conditions</span>{' '}
                on the next page before purchasing. Click{' '}
                <span className="fw-bold">Buy</span> to proceed.
              </div>
              <Button
                primary
                fluid
                className="mt-12"
                disabled={!completed}
                loading={isSubmitting}
                onClick={() => {
                  setShowTnd(true);
                  showTndRef.current = true;
                }}
              >
                Buy
              </Button>
              {showTndRef.current && (
                <div className="mt-12 text-center">
                  <WarningIcon />
                  <span
                    style={{ color: '#B60000' }}
                    className="fw-bold fs-14 ml-2"
                  >
                    You must agree to our Terms and Conditions to proceed with
                    your purchase
                  </span>
                </div>
              )}
            </Form>
          </Grid.Column>

          <Grid.Column
            width="7"
            className={!screen.sm ? 'border-none' : undefined}
          >
            <PaymentCourseInfo
              {...course}
              expired_date={expired_date}
              isExtension={isExtension}
              amount={paymentIntent?.amount}
              created={paymentIntent?.created}
            />
          </Grid.Column>
        </Grid>
      </Segment>
    </div>
  );
};

const WarningIcon = () => {
  return (
    <svg
      width="10"
      height="10"
      viewBox="0 0 10 10"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      xmlnsXlink="http://www.w3.org/1999/xlink"
    >
      <rect width="10" height="10" fill="url(#pattern0)" />
      <defs>
        <pattern
          id="pattern0"
          patternContentUnits="objectBoundingBox"
          width="1"
          height="1"
        >
          <use xlinkHref="#image0_124_32" transform="scale(0.00195312)" />
        </pattern>
        <image
          id="image0_124_32"
          width="512"
          height="512"
          xlinkHref="data:image/png;base64,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"
        />
      </defs>
    </svg>
  );
};

export default PaymentForm;
